import { useId, useMemo } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useScheduleIssues } from "../api/use-issues";
import { useRun } from "../api/use-runs";
import {
  computeAssignmentDelta,
  computeIssueDelta,
  computeNetChange,
} from "../lib/compute-run-delta";
import type { ScheduleValidationIssue } from "../types";

interface RunCompareCardProps {
  scheduleId: string;
  currentRunId: string;
  priorRunId: string;
  /** Display label for the current (newer) run, e.g. `"Run #4"`. */
  currentRunLabel: string;
  /** Display label for the prior (older) run, e.g. `"Run #3"`. */
  priorRunLabel: string;
  onClose: () => void;
}

/**
 * Filters a schedule-wide issue list down to a single run's PostRun issues.
 * Stage 1 / Stage 2 issues carry no run context and are filtered out — only
 * Stage 3 (`PostRun`) issues participate in the run-comparison delta.
 */
function filterPostRunIssuesForRun(
  issues: ScheduleValidationIssue[] | undefined,
  runId: string,
): ScheduleValidationIssue[] {
  if (!issues) return [];
  return issues.filter((i) => i.stage === "PostRun" && i.runId === runId);
}

/**
 * Formats a signed integer for display with the leading sign explicit:
 *  - positive →`"+5"`
 *  - zero     → `"0"`
 *  - negative → `"-5"` (uses the standard hyphen-minus; JS `toString` handles it).
 */
function formatSignedNetChange(value: number): string {
  if (value > 0) return `+${value}`;
  return value.toString();
}

/**
 * Single KPI cell in the 4-metric grid. Pure presentational subcomponent —
 * extracted so all four cells share the same border, padding, and label color
 * tokens (and so the value-color rule is the only thing that varies per cell).
 */
function KpiCell({
  value,
  valueColorClass,
  label,
  testId,
}: {
  value: string;
  valueColorClass: string;
  label: string;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className="bg-page border border-border-subtle rounded-md p-3 sm:p-4"
    >
      <div
        data-testid={`${testId}-value`}
        className={`text-[26px] font-bold leading-none ${valueColorClass}`}
      >
        {value}
      </div>
      <div className="mt-2 text-[12px] text-text-muted">{label}</div>
    </div>
  );
}

/**
 * Compare card — appears above the runs table when the user clicks
 * "Compare to #N-1" on a row. Fetches both runs in parallel via {@link useRun}
 * + a single schedule-wide issues query, then computes four KPIs client-side
 * via pure functions in `lib/compute-run-delta.ts`.
 *
 * Loading / error states mount inside the same card chrome so the user can
 * see what's being compared while it loads.
 */
export function RunCompareCard({
  scheduleId,
  currentRunId,
  priorRunId,
  currentRunLabel,
  priorRunLabel,
  onClose,
}: RunCompareCardProps) {
  const headingId = useId();
  const currentQuery = useRun(scheduleId, currentRunId);
  const priorQuery = useRun(scheduleId, priorRunId);
  const issuesQuery = useScheduleIssues(scheduleId);

  // PostRun issues per run, derived from the schedule-wide issues list.
  // useScheduleIssues is already populated whenever the user reaches the runs
  // tab (it backs the Validation Issues tab on the same page) — reusing it
  // avoids a separate per-run issues fetch.
  const currentPostRunIssues = useMemo(
    () => filterPostRunIssuesForRun(issuesQuery.data, currentRunId),
    [issuesQuery.data, currentRunId],
  );
  const priorPostRunIssues = useMemo(
    () => filterPostRunIssuesForRun(issuesQuery.data, priorRunId),
    [issuesQuery.data, priorRunId],
  );

  const deltas = useMemo(() => {
    if (!currentQuery.data || !priorQuery.data) return null;
    const { studentsReassigned } = computeAssignmentDelta(
      currentQuery.data.assignments,
      priorQuery.data.assignments,
    );
    const { conflictsResolved, newConflicts } = computeIssueDelta(
      currentPostRunIssues,
      priorPostRunIssues,
    );
    const netChange = computeNetChange(currentQuery.data, priorQuery.data);
    return { studentsReassigned, conflictsResolved, newConflicts, netChange };
  }, [
    currentQuery.data,
    priorQuery.data,
    currentPostRunIssues,
    priorPostRunIssues,
  ]);

  const isLoading =
    currentQuery.isLoading || priorQuery.isLoading || issuesQuery.isLoading;
  const isError =
    currentQuery.isError || priorQuery.isError || issuesQuery.isError;

  const headerLabel = `${currentRunLabel} compared to ${priorRunLabel}`;

  return (
    <section
      data-testid="run-compare-card"
      aria-labelledby={headingId}
      className="bg-surface border border-blue-500/40 rounded-lg p-5 mb-5"
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          id={headingId}
          data-testid="run-compare-card-heading"
          className="m-0 text-[15px] font-semibold text-white"
        >
          {headerLabel}
        </h3>
        <button
          type="button"
          onClick={onClose}
          data-testid="run-compare-card-close"
          aria-label="Close compare card"
          className="text-text-muted hover:text-white text-[13px] px-2 py-1 rounded"
        >
          Close
        </button>
      </div>

      {isLoading && !deltas && (
        <div
          data-testid="run-compare-card-loading"
          className="py-8 flex justify-center"
        >
          <LoadingSpinner size="md" />
        </div>
      )}

      {isError && !isLoading && (
        <div
          data-testid="run-compare-card-error"
          className="bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 text-sm text-red-400"
        >
          Failed to load runs for comparison.
        </div>
      )}

      {deltas && (
        <div
          data-testid="run-compare-card-grid"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <KpiCell
            testId="run-compare-kpi-reassigned"
            value={deltas.studentsReassigned.toString()}
            valueColorClass="text-blue-400"
            label="students re-assigned"
          />
          <KpiCell
            testId="run-compare-kpi-resolved"
            value={deltas.conflictsResolved.toString()}
            valueColorClass="text-emerald-400"
            label="conflicts resolved"
          />
          <KpiCell
            testId="run-compare-kpi-new"
            value={deltas.newConflicts.toString()}
            valueColorClass="text-red-400"
            label="new conflicts"
          />
          <KpiCell
            testId="run-compare-kpi-net"
            value={formatSignedNetChange(deltas.netChange)}
            valueColorClass={
              deltas.netChange > 0 ? "text-red-400" : "text-emerald-400"
            }
            label="net issue change"
          />
        </div>
      )}
    </section>
  );
}
